import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../assets/stylesheets/realestate.css";
import {FaMapMarkerAlt,  FaRulerCombined, FaBuilding, FaStar} from "react-icons/fa";
import {routes} from "../../routes";
import {GiReceiveMoney} from "react-icons/gi";
import {IoBed} from "react-icons/io5";

function EstateList({ type, language, filter: providedFilter, query = ""  }) {
    const { t } = useTranslation(["estate"]);
    const [estates, setEstates] = useState([]);
    const [page, setPage] = useState(0);
    const [size] = useState(12);
    const [totalPages, setTotalPages] = useState(1);
    const [sort, setSort] = useState(""); // New state for sorting

    // Add state for favorites
    const [favoriteIds, setFavoriteIds] = useState(() => {
        const storedFavorites = localStorage.getItem("favoriteEstates");
        return storedFavorites ? JSON.parse(storedFavorites) : [];
    });

    // Update local storage when favoriteIds changes
    useEffect(() => {
        localStorage.setItem("favoriteEstates", JSON.stringify(favoriteIds));
    }, [favoriteIds]);

    // Add toggleFavorite function
    const toggleFavorite = (id) => {
        setFavoriteIds((prev) => {
            if (prev.includes(id)) {
                return prev.filter((favId) => favId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // Use a stable filter if none is provided
    const filter = useMemo(() => providedFilter || new Map(), [providedFilter]);

    useEffect(() => {
        if (type === "search" && !query) {
            console.log("EstateList: Skipping API call due to empty query for search type");
            return;
        }

        const fetchEstates = async () => {
            try {
                const searchParams = new URLSearchParams();
                filter.forEach((values, key) => {
                    if (['language', 'page', 'size', 'sort'].includes(key)) return;
                    if (values && Array.isArray(values) && values.length > 0) {
                        searchParams.append(`${key}`, values.join(","));
                    }
                });
                searchParams.append("language", language);
                searchParams.append("page", page);
                searchParams.append("size", size);
                if (sort) {
                    searchParams.append("sort", sort); // Add sort parameter
                }

                console.log("EstateList: Params ready to send to api:", searchParams.toString());

                let endpoint;
                if (type === "search" || type === "search-top") {
                    endpoint = "search";
                    if (query) {
                        searchParams.append("query", query);
                    }
                } else if (type === "favorites") {
                    endpoint = "favorites";
                    if (query) {
                        searchParams.append("ids", query); // Use 'ids' for favorite endpoint
                    } else {
                        console.log("EstateList: Skipping API call due to empty query for favorite type");
                        setEstates([]);
                        setTotalPages(1);
                        return;
                    }
                } else {
                    endpoint = `${type}/getAll`;
                }

                const res = await axios.get(
                    `${process.env.REACT_APP_MY_API_KAY}/${endpoint}?${searchParams.toString()}`
                );

                console.log("Received: ", res.data.content);

                setEstates(res.data.content);
                setTotalPages(res.data.totalPages);
            } catch (error) {
                console.error("Error fetching estates:", error);
            }
        };
        fetchEstates();
    }, [type, language, page, size, filter, query, sort]); // Add sort to dependencies

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    const handleSortChange = (event) => {
        setSort(event.target.value);
        setPage(0); // Reset to first page when sorting changes
    };

    const formatPrice = (value, currency) => {
        if (!value) return "";
        return `${currency} ${Number(value).toLocaleString("en-US")}`;
    };

    const formatArea = (value) => {
        if (value == null) return "";
        const num = Number(value);
        return num % 1 === 0 ? `${num.toFixed(0)}` : `${num}`;
    };


    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
    };

    const defaultImages = [
        { url: "https://thumbs.dreamstime.com/b/residential-real-estate-buying-selling-renting-homes-apartments-condos-townhouses-investment-shelter-expert-guidance-318813833.jpg", imageKey: "default1", main: false },
        { url: "https://thumbs.dreamstime.com/b/residential-real-estate-buying-selling-renting-homes-apartments-condos-townhouses-investment-shelter-expert-guidance-318813833.jpg", imageKey: "default2", main: false },
    ];

    function getBaseUrlFromRouteType(type, lang) {
        return routes[type]?.[lang] || null;
    }

    return (
        <div className="estate__list-content">
            <div className="estate__list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="estate__list-title">
                    {type === 'search-top' ? '' : t(`${type}-title`)}
                </h3>


                <select className="estate__sort-select"
                    value={sort}
                    onChange={handleSortChange}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                    <option value="" disabled hidden>
                        {t("sort.default")}
                    </option>
                    <option value="PRICE_ASC">{t("sort.price_asc")}</option>
                    <option value="PRICE_DESC">{t("sort.price_desc")}</option>
                    <option value="AREA_ASC">{t("sort.area_asc")}</option>
                    <option value="AREA_DESC">{t("sort.area_desc")}</option>
                    <option value="NAME_ASC">{t("sort.name_asc")}</option>
                    <option value="NAME_DESC">{t("sort.name_desc")}</option>
                </select>
            </div>

            <div className="estate__grid">
                {estates.map((estate) => {
                    const images = (estate.images && estate.images.length > 0 ? estate.images : defaultImages);


                    const areaUnit = Object.values(estate.attributes || {}).find(
                        attr => attr.attributeType === "AREA_UNIT" && attr.show && attr.name && attr.name !== "null"
                    );
                    const price = Object.values(estate.attributes || {}).find(
                        attr => attr.attributeType === "PRICE" && attr.show && attr.name && attr.name !== "null"
                    );
                    const floorAttr = Object.values(estate.attributes || {}).find(attr => attr.attributeType === "FLOOR" && attr.show && attr.name && attr.name !== "null");
                    const floorsAttr = Object.values(estate.attributes || {}).find(attr => attr.attributeType === "FLOORS" && attr.show && attr.name && attr.name !== "null");
                    const floors = floorsAttr ? floorsAttr.name : "";

                    const route = getBaseUrlFromRouteType(estate.type, language);

                    return (
                        <div key={estate.id} className="estate__card">
                            <Link to={`/${language}/${route}/${estate.id}`}>
                                <div className="estate__card-carousel">

                                    {estate.exclusive && (
                                        <span className="estate__exclusive-badge">{t("exclusive-title")}</span>
                                    )}
                                    {estate.crmNumber && (
                                        <span className={`estate__crm-number ${!estate.exclusive ? 'estate__crm-number--top' : ''}`}>
                                          {t("estate.codeLabel")}  {estate.crmNumber}
                                        </span>
                                    )}

                                    {estate.type === 'business' && (
                                        <span className="estate__type-badge"> {t("estate:business-title")}
                                            {estate.working && ` - ${t("estate:working-title")}`}
                                </span>
                                    )}

                                    <Slider {...sliderSettings}>
                                        {images.length > 0 && (
                                            <div key={images[0].imageKey}>
                                                <img
                                                    src={images[0].url}
                                                    alt={estate.title}
                                                    className="estate__card-image"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                    </Slider>
                                    <button
                                        className={`estate__favorite-icon ${favoriteIds.includes(estate.id) ? "estate__favorite-icon--active" : ""}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleFavorite(estate.id);
                                        }}
                                    >
                                        <FaStar />
                                    </button>
                                </div>
                                <div className="estate__card-content">
                                    <h4 className="estate__card-title">{estate.title}</h4>
                                    <p className="estate__card-address">
                                        <FaMapMarkerAlt className="estate__card-icon" />
                                        {[
                                            estate?.address,
                                            ...Object.entries(estate.attributes)
                                                .filter(
                                                    ([_, attr]) =>
                                                        attr.show &&
                                                        (attr.attributeType === "ADDRESS")
                                                )
                                                .map(([_, attr]) => attr.name)
                                        ]
                                            .filter(value => value != null && value !== '')
                                            .join(', ')}
                                    </p>
                                    <p className="estate__card-type">
                                        <GiReceiveMoney className="estate__card-icon" />
                                        {estate.type === 'business' ? (
                                            Object.values(estate.attributes || {}).some(
                                                attr => attr.show && attr.attributeType === 'RENT_TYPE'
                                            ) ? (
                                                t('estate:rent-title')
                                            ) : (
                                                t('estate:sales-title')
                                            )
                                        ) : (
                                            t(`estate:${estate.type}-title`)
                                        )}

                                        {(estate.type === 'rent' ||
                                            (estate.type === 'business' && Object.values(estate.attributes || {}).some(
                                                attr => attr.show && attr.attributeType === 'RENT_TYPE'
                                            ))
                                        ) && (
                                            <>
                                                {' - '}
                                                {Object.entries(estate.attributes || {})
                                                    .filter(
                                                        ([_, attr]) =>
                                                            attr.show && attr.attributeType === 'RENT_TYPE'
                                                    )
                                                    .map(([_, attr]) => attr.name)
                                                    .filter(value => value != null && value !== '')
                                                    .join(', ')}
                                            </>
                                        )}
                                    </p>
                                    <p className="estate__card-price">
                                        {formatPrice(price.name, estate.currency)}
                                    </p>
                                    <div className="estate__card-info">
                                        {Object.entries(estate.attributes)
                                            .filter(([_, attr]) => attr.show)
                                            .map(([key, attr]) => {
                                                if (attr.attributeType === "AREA_SIZE") {
                                                    return (
                                                        <p key={key} className="estate__card-attribute">
                                                            <FaRulerCombined className="estate__card-icon" />
                                                            {formatArea(attr.name)} {areaUnit ? areaUnit.name : "m²"}
                                                        </p>
                                                    );
                                                }
                                                if (attr.attributeType === "ROOMS") {
                                                    return (
                                                        <p key={key} className="estate__card-attribute">
                                                            <IoBed  className="estate__card-icon" />
                                                            {t(`estate:propertyRooms`)} {attr.name}
                                                        </p>
                                                    );
                                                }
                                                if (attr.attributeType === "FLOOR" && floors) {
                                                    return (
                                                        <p key={key} className="estate__card-attribute">
                                                            <FaBuilding className="estate__card-icon" />
                                                            {t(`estate:propertyFloor`)} {attr.name}/{floors}
                                                        </p>
                                                    );
                                                }
                                                return null;
                                            })}
                                    </div>
                                </div>
                            </Link>
                        </div>
                    );
                })}
            </div>
            {totalPages > 1 && (
                <div className="pagination__container">
                    <button
                        className="pagination__button"
                        disabled={page === 0}
                        onClick={() => handlePageChange(page - 1)}
                    >
                        {t("previous")}
                    </button>
                    <span className="pagination__info">
                        {t("page")} {page + 1} {t("of")} {totalPages}
                    </span>
                    <button
                        className="pagination__button"
                        disabled={page >= totalPages - 1}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        {t("next")}
                    </button>
                </div>
            )}
        </div>
    );
}

export default EstateList;